import { createTheme } from "@mui/material/styles";

/**
 * Central app theme — the single source of truth for colour, type and shape.
 *
 * Design intent (see docs/UI_PROFESSIONALISM_REVIEW.md):
 *  - Restraint over decoration. One confident accent, lots of neutral space —
 *    the cards are the product, the chrome should recede (StockX / Vinted).
 *  - No candy gradients or heavy coloured glows; subtle borders + soft shadows.
 *  - Buttons are sentence-case, medium weight — not ALL-CAPS 900 (Stripe/Linear).
 *  - Muted semantic colours so statuses read as information, not alarm.
 */

// Neutral slate ramp — the backbone of a trustworthy, calm UI.
const ink = {
  900: "#0f172a",
  700: "#334155",
  500: "#64748b",
  300: "#cbd5e1",
  100: "#f1f5f9",
  50: "#f8fafc",
};

const theme = createTheme({
  // Phone-only product: push every breakpoint above `xs` out of reach so the
  // whole app always resolves to its mobile (xs) layout — on desktop it renders
  // the same phone UI (centered by Layout) instead of a separate 3-column
  // desktop layout we no longer maintain. Zero effect on real phones (they were
  // always below `sm`); it only forces larger viewports to match.
  breakpoints: {
    values: { xs: 0, sm: 100000, md: 100001, lg: 100002, xl: 100003 },
  },

  palette: {
    mode: "light",
    primary: {
      // A mature, deep violet — the brand accent, used sparingly.
      main: "#6d28d9",
      dark: "#5b21b6",
      light: "#8b5cf6",
      contrastText: "#ffffff",
    },
    secondary: {
      main: ink[700],
      contrastText: "#ffffff",
    },
    success: { main: "#16a34a" },
    warning: { main: "#d97706" },
    error: { main: "#dc2626" },
    info: { main: "#2563eb" },
    text: {
      primary: ink[900],
      secondary: ink[500],
    },
    background: {
      default: ink[50],
      paper: "#ffffff",
    },
    divider: "rgba(15,23,42,0.08)",
  },

  shape: { borderRadius: 10 },

  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    // Restrained weight scale — headings are semibold, not black.
    h1: { fontWeight: 700, letterSpacing: "-0.02em" },
    h2: { fontWeight: 700, letterSpacing: "-0.02em" },
    h3: { fontWeight: 700, letterSpacing: "-0.01em" },
    h4: { fontWeight: 700, letterSpacing: "-0.01em" },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: "none" },
  },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, paddingInline: 18 },
      },
    },
    // B-05: a field takes its direction from what is typed into it, so Hebrew
    // reads right to left ("בסדר?" ends with its "?" on the left) while English
    // and the app's own layout stay left to right. On InputBase, so it reaches
    // every TextField, multi-line box, search bar and Autocomplete; and through
    // `slotProps.input`, which MUI merges on top of a field's own `inputProps`
    // (InputBase.js), so a field that passes a maxLength keeps it.
    MuiInputBase: {
      defaultProps: { slotProps: { input: { dir: "auto" } } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    // E2E audit 7·3: every avatar in the app sits beside the person's name, so
    // its picture is decorative. Without an alt the image itself was announced
    // (for generated avatars, a data: URL). A caller that names one keeps it.
    MuiAvatar: {
      defaultProps: { alt: "" },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 12 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: ink[900], fontSize: "0.72rem" },
      },
    },
  },
});

export default theme;
